import React, { useEffect, useState } from 'react';
import { Table, Button, message, Input, Tag, Card, Typography } from 'antd';
import { PlusOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useOrders } from '../../hooks/useManufacturing';
import useAuth from '../../hooks/useAuth';

const { Title } = Typography;

const ViewOrdersList: React.FC = () => {
  const { config } = useConfig();
  const { company } = useCompany();
  const history = useHistory();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const [searchText, setSearchText] = useState('');

  const { data, isLoading, error } = useOrders(tokenData, { company: company.current?.id });

  useEffect(() => {
    if (error) {
      message.error("Error al cargar las órdenes de producción");
    }
  }, [error]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'orange';
      case 'IN_PROGRESS': return 'blue';
      case 'COMPLETED': return 'green';
      case 'CANCELLED': return 'red';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'No. Orden',
      dataIndex: 'document_number',
      key: 'document_number',
      width: 140,
      render: (val: any) => <strong>{val || 'S/N'}</strong>
    },
    {
      title: 'Plantilla Base',
      dataIndex: ['production_templates', 'name'],
      key: 'template_name',
      width: 200,
      render: (val: any) => val || 'Sin plantilla'
    },
    {
      title: 'Cant. Esperada',
      dataIndex: 'expected_quantity',
      key: 'expected_quantity',
      width: 130,
      align: 'center' as const,
    },
    {
      title: 'Cant. Real',
      dataIndex: 'actual_quantity',
      key: 'actual_quantity',
      width: 130,
      align: 'center' as const,
      render: (val: any) => val || '-'
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      align: 'center' as const,
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 130,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Button 
          type="primary" 
          ghost 
          size="small"
          icon={<EditOutlined />} 
          onClick={() => history.push(`/edit-order/${record.uid || record.id}`)}
        >
          Ver/Editar
        </Button>
      ),
    },
  ];

  const filteredData = data?.orders?.filter((order: any) => 
    order.id?.toString().includes(searchText) ||
    order.production_templates?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    order.status?.toLowerCase().includes(searchText.toLowerCase())
  ) || [];

  return (
    <div style={{ padding: config.isMobile ? '4px 0' : '0' }}>
      <Card bodyStyle={{ padding: config.isMobile ? '14px 10px' : '24px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          gap: '12px',
          marginBottom: '16px' 
        }}>
          <Title level={config.isMobile ? 4 : 3} style={{ margin: 0 }}>
            Órdenes de Producción
          </Title>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            style={{ width: config.isMobile ? '100%' : 'auto', height: config.isMobile ? '38px' : 'auto' }}
            onClick={() => history.push('/create-order')}
          >
            Nueva Orden
          </Button>
        </div>
        
        <div style={{ marginBottom: '16px', maxWidth: config.isMobile ? '100%' : '380px' }}>
          <Input 
            placeholder="Buscar orden (ID, Plantilla, Estado)..." 
            prefix={<SearchOutlined />} 
            allowClear
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        </div>

        <Table 
          columns={columns} 
          dataSource={filteredData} 
          rowKey="id" 
          loading={isLoading}
          size={config.isMobile ? 'small' : 'middle'}
          scroll={{ x: 800 }}
          pagination={{ 
            showSizeChanger: true, 
            defaultPageSize: 10, 
            pageSizeOptions: ['10', '20', '50'],
            size: config.isMobile ? 'small' : 'default'
          }}
        />
      </Card>
    </div>
  );
};

export default ViewOrdersList;